import { AlfaIcon } from '@p2p/assets/svg';
import { notify } from '@p2p/components';
import { hasProviderQrImage } from '@p2p/entities/qrInvoice/providerQrImage';
import { PaymentCancelModal, usePaymentTemplate } from '@p2p/entities/templates';
import { TGetOrderResponse, TPageSettingsResponse } from '@p2p/services';
import { addZero } from '@p2p/shared/functions';
import { useDeviceDetect } from '@p2p/shared/hooks';
import { routes } from '@p2p/shared/routing';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { useNavigate } from 'react-router-dom';

import { ModalBankTranfer } from '../TransferForm/ModalBankTranfer/ModalBankTranfer';
import { ModalIncrementApplied } from '../TransferForm/ModalIncrementApplied/ModalIncrementApplied';
import { TransferUpload } from '../TransferForm/TransferUpload';
import {
  getRequisiteRows,
  QRReturnModal,
  type RequisiteRow,
  SberQrInvoiceWarningModal,
} from '../TransferInvoicePage/components';
import { generateRandomId } from '../TransferInvoicePage/components/QRCodeInvoice/QRCodeInvoice.utils';
import { getQrInvoiceSource } from '../TransferInvoicePage/components/QRCodeInvoice/qrInvoiceSource';
import { type AlfaPlatform, detectAlfaPlatform } from './alfaApp';
import styles from './AlfaQrInvoice.module.scss';
import { AlfaRequisites } from './AlfaRequisites';
import { HowToPay } from './HowToPay';
import { CheckMarkIcon, DownloadQrIcon, ScanIcon, TimerIcon, WarnIcon } from './icons';
import { getQrImageSrc, renderQrPng, saveQrFile } from './qrImage';
import { useOpenAlfaApp } from './useOpenAlfaApp';

type AlfaQrInvoiceProps = {
  order: TGetOrderResponse;
  paramId: string;
  pageSettings: TPageSettingsResponse;
};

const CURRENCY_SIGNS: Record<string, string> = { RUB: '₽' };

export const formatAlfaAmount = (amount: number, currency?: string): string => {
  const code = (currency || 'RUB').toUpperCase();

  return `${Number(amount).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${
    CURRENCY_SIGNS[code] ?? code
  }`;
};

const formatTimeLeft = (seconds: number): string =>
  `${addZero(Math.floor(seconds / 60))}:${addZero(seconds % 60)}`;

const savedLabel = (result: 'shared' | 'downloaded', platform: AlfaPlatform | null): string => {
  if (result === 'shared') {
    return 'QR сохранён в Фото';
  }

  return platform ? 'QR сохранён в галерею' : 'QR скачан';
};

/**
 * P2P-3116: экран оплаты по QR на странице alfa_page (main_page в новом дизайне, прототип из ТЗ).
 * Рисуется вместо TransferInvoice там же, где он: реквизит с qr_invoice (и другие заявки экрана
 * «Оплата по QR-коду»). QR, реквизиты, «Я оплатил», отмена и модалки - те же, что у TransferInvoice.
 *
 * Телефон и планшет (iOS/Android по User-Agent) всегда получают мобильную вёрстку, даже шире 760 px;
 * компьютер от 760 px - две колонки. Обе вёрстки в разметке, показ - через CSS.
 */
export const AlfaQrInvoice = ({ order, paramId, pageSettings }: AlfaQrInvoiceProps) => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceDetect();
  const { setActiveIndex } = usePaymentTemplate();
  const [platform] = useState(() => detectAlfaPlatform());
  const [qrRandomId] = useState(() => generateRandomId());
  // QR и номер назначения не меняются, пока открыта страница (как у QrCodeInvoice, memo по id).
  const [qrSource] = useState(() => getQrInvoiceSource(order, qrRandomId));
  const isProviderQrImageOrder = hasProviderQrImage(order);
  const [isShowCancelModal, setIsShowCancelModal] = useState(false);
  const [isShowUpload, setIsShowUpload] = useState(false);
  const [isModalIncrementAppliedOpen, setIsModalIncrementAppliedOpen] = useState(true);
  // Без QR (банк без QR) реквизиты - единственный способ оплаты, блок открыт сразу.
  const [isRequisitesOpen, setIsRequisitesOpen] = useState(() => !qrSource);
  const [savedText, setSavedText] = useState<string | null>(null);
  const qrBoxRef = useRef<HTMLDivElement>(null);
  const qrFileRef = useRef<File | null>(null);
  const requisitesRef = useRef<HTMLElement>(null);
  const { openAlfa, isOpening } = useOpenAlfaApp({ orderId: order.id, platform });

  const amountText = formatAlfaAmount(order.expected_income, order.currency_code_to);
  const orderNumber = order.id.slice(0, 8).toUpperCase();

  // P2P-2511/2846/3043: у готового QR провайдера, ссылки на оплату и картинки провайдера реквизит
  // технический - блок реквизитов не показываем (как в TransferInvoice).
  const requisiteRows = useMemo<RequisiteRow[]>(() => {
    if (order.emvco || order.payment_via_link || isProviderQrImageOrder) {
      return [];
    }

    const rows = getRequisiteRows(order, qrRandomId);

    return rows.length
      ? [...rows, { label: 'Сумма', value: String(order.expected_income), canCopy: true }]
      : [];
  }, [order, qrRandomId, isProviderQrImageOrder]);

  // Заявка опрашивается раз в секунду - QR не перерисовываем (он не меняется).
  const [qrNode, miniQrNode] = useMemo(() => {
    const renderQr = (size: number) =>
      qrSource?.kind === 'image' ? (
        <img src={qrSource.src} alt="QR-код для оплаты" className={styles.qrImage} />
      ) : (
        <QRCode value={qrSource?.value ?? ''} size={size} className={styles.qrImage} />
      );

    return qrSource ? [renderQr(256), renderQr(96)] : [null, null];
  }, [qrSource]);

  useEffect(() => {
    setActiveIndex(2);
  }, [setActiveIndex]);

  const buildQrFile = useCallback(async (): Promise<File | null> => {
    const qrSrc = getQrImageSrc(qrBoxRef.current);

    if (!qrSrc) {
      return null;
    }

    const blob = await renderQrPng({ qrSrc, amount: amountText, orderNumber });

    return blob ? new File([blob], `qr-oplata-${orderNumber}.png`, { type: 'image/png' }) : null;
  }, [amountText, orderNumber]);

  // Картинку готовим заранее: меню «Поделиться» на iOS открывается только сразу по нажатию.
  useEffect(() => {
    let isActual = true;

    buildQrFile()
      .then((file) => {
        if (isActual) {
          qrFileRef.current = file;
        }
      })
      .catch(() => {
        qrFileRef.current = null;
      });

    return () => {
      isActual = false;
    };
  }, [buildQrFile]);

  const handleSaveQr = async (savePlatform: AlfaPlatform | null) => {
    let file = qrFileRef.current;

    if (!file) {
      file = await buildQrFile().catch(() => null);
    }

    if (!file) {
      notify({ type: 'warning', title: 'Не удалось сохранить QR — сделайте снимок экрана' });
      return;
    }

    const result = await saveQrFile(file, savePlatform);

    if (result !== 'cancelled') {
      setSavedText(savedLabel(result, savePlatform));
    }
  };

  const handlePaid = () => {
    const transferUploadFiles = JSON.parse(localStorage.getItem('transfer_upload_files') || '{}');
    const files = transferUploadFiles.orderId !== paramId ? 0 : transferUploadFiles?.files;

    if (transferUploadFiles.orderId !== paramId) {
      localStorage.removeItem('transfer_upload_files');
    }

    if (pageSettings.receipt && files < 3) {
      setIsShowUpload(true);
    } else {
      navigate(routes.transferSearch.path({ id: paramId }));
    }
  };

  const openRequisites = () => {
    setIsRequisitesOpen(true);
    setTimeout(() => requisitesRef.current?.scrollIntoView?.({ behavior: 'smooth' }), 0);
  };

  if (isShowUpload) {
    return <TransferUpload order={order} handleBack={() => setIsShowUpload(false)} />;
  }

  const warning = (
    <div className={styles.warn}>
      <WarnIcon />
      <span>Не меняйте сумму — иначе платёж потеряется</span>
    </div>
  );

  const actions = (
    <div className={styles.foot}>
      <button
        type="button"
        className={classNames(styles.button, styles.buttonPrimary)}
        onClick={handlePaid}
      >
        Я оплатил
      </button>
      <div className={styles.links}>
        <button type="button" onClick={() => setIsShowCancelModal(true)}>
          Отменить
        </button>
        {requisiteRows.length > 0 && (
          <button type="button" className={styles.requisitesLink} onClick={openRequisites}>
            Оплатить по реквизитам
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className={classNames(styles.page, { [styles.touch]: Boolean(platform) })}>
        <section className={styles.card}>
          <div className={styles.head}>
            <div>
              <div className={styles.label}>К оплате</div>
              <div className={styles.amount}>{amountText}</div>
            </div>
            {order.time_left !== undefined && (
              <div className={styles.timer} data-testid="alfa-timer">
                <TimerIcon />
                <span>{formatTimeLeft(Math.max(0, order.time_left))}</span>
              </div>
            )}
          </div>

          <div className={styles.mobileOnly}>
            {qrSource && (
              <>
                <div className={styles.qr} ref={qrBoxRef}>
                  {qrNode}
                </div>
                <div className={styles.buttons}>
                  <button
                    type="button"
                    className={classNames(styles.button, styles.buttonSoft, {
                      [styles.buttonSolo]: !platform,
                      [styles.buttonSaved]: Boolean(savedText),
                    })}
                    onClick={() => handleSaveQr(platform)}
                  >
                    {savedText ? <CheckMarkIcon /> : <DownloadQrIcon />}
                    <span>{savedText ?? 'Скачать QR'}</span>
                  </button>
                  {platform === 'ios' && !savedText && (
                    <p className={styles.shareHint}>
                      В открывшемся меню нажмите <b>«Сохранить изображение»</b>
                    </p>
                  )}
                  {platform && (
                    <button
                      type="button"
                      className={classNames(styles.button, styles.buttonAlfa)}
                      onClick={openAlfa}
                      disabled={isOpening}
                    >
                      <AlfaIcon className={styles.alfaLogo} aria-hidden />
                      Открыть Альфа-Банк
                    </button>
                  )}
                </div>
                <HowToPay platform={platform} miniQr={miniQrNode} />
              </>
            )}
            {warning}
            {actions}
          </div>

          <div className={styles.desktopOnly}>
            <div className={classNames(styles.grid, { [styles.gridNoQr]: !qrSource })}>
              {qrSource && (
                <div>
                  <div className={styles.qr}>{qrNode}</div>
                  <button
                    type="button"
                    className={styles.downloadLink}
                    onClick={() => handleSaveQr(null)}
                  >
                    {savedText ?? 'Скачать QR'}
                  </button>
                </div>
              )}
              <div>
                {qrSource && (
                  <ol className={styles.desktopSteps}>
                    <li>
                      <span className={styles.stepNumber}>1</span>
                      <span>
                        Откройте <b>Альфа-Банк</b> на телефоне
                      </span>
                    </li>
                    <li>
                      <span className={styles.stepNumber}>2</span>
                      <span>
                        Нажмите{' '}
                        <span className={styles.ringInline}>
                          <ScanIcon />
                        </span>{' '}
                        справа от поиска
                      </span>
                    </li>
                    <li>
                      <span className={styles.stepNumber}>3</span>
                      <span>Наведите камеру на QR</span>
                    </li>
                    <li>
                      <span className={styles.stepNumber}>4</span>
                      <span>Подтвердите платёж</span>
                    </li>
                  </ol>
                )}
                {warning}
                {actions}
              </div>
            </div>
          </div>
        </section>

        {requisiteRows.length > 0 && (
          <AlfaRequisites
            ref={requisitesRef}
            rows={requisiteRows}
            isOpen={isRequisitesOpen}
            onToggle={() => setIsRequisitesOpen((isOpen) => !isOpen)}
          />
        )}
      </div>
      <PaymentCancelModal
        orderStatus={order.status}
        isActive={isShowCancelModal}
        setIsActive={setIsShowCancelModal}
        pageSettings={pageSettings}
        orderId={order.id}
      />
      <SberQrInvoiceWarningModal order={order} />
      {isMobile && (
        <QRReturnModal onSuccess={handlePaid} onClose={() => setIsRequisitesOpen(true)} />
      )}
      {pageSettings.notification &&
        !isShowCancelModal &&
        !(isModalIncrementAppliedOpen && order.increment_applied) && (
          <ModalBankTranfer order={order} />
        )}
      {order.id && order.increment_applied && (
        <ModalIncrementApplied
          uid={order.id}
          sumText={`${order.expected_income.toLocaleString('ru-RU')} ${order?.currency_code_to}`}
          isCancelModalOpen={isShowCancelModal}
          showCancelModal={() => setIsShowCancelModal(true)}
          isOpen={isModalIncrementAppliedOpen}
          setIsOpen={setIsModalIncrementAppliedOpen}
        />
      )}
    </>
  );
};
